import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiService } from "../../services/api.service";
import { toast } from 'react-toastify';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

// 9 idiomas soportados
const languages = [
  { code: "es", label: "Español" },
  { code: "en", label: "English" },
  { code: "pt", label: "Português" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "nl", label: "Nederlands" },
  { code: "ru", label: "Русский" },
  { code: "ar", label: "العربية" }
];

const Setting = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [savingLanguage, setSavingLanguage] = useState(false);
  
  // Estados para 2FA
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFASecret, setTwoFASecret] = useState("");
  const [qrCodeData, setQrCodeData] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [tempBackupCodes, setTempBackupCodes] = useState<string[]>([]); // Para guardar temporalmente durante setup
  const [show2FASuccess, setShow2FASuccess] = useState(false); // Para mostrar pantalla de éxito
  const [loading2FA, setLoading2FA] = useState(false);
  
  // Estados para idioma
  const [language, setLanguage] = useState("en");
  
  // Estados para cambio de contraseña
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Cargar datos iniciales
  useEffect(() => {
    if (user) {
      // Cargar estado de 2FA
      load2FAStatus();
      // Cargar idioma preferido
      setLanguage(user.preferredLanguage || "en");
    }
  }, [user]);

  // Cargar estado de 2FA
  const load2FAStatus = async () => {
    try {
      const response = await apiService.get('/auth/2fa/status');
      setTwoFAEnabled(response.enabled || false);
    } catch (error) {
      console.error('Error loading 2FA status:', error);
    }
  };

  // Configurar 2FA - obtener QR code
  const setup2FA = async () => {
    setLoading2FA(true);
    // Resetear estados para asegurar flujo limpio
    setShow2FASuccess(false);
    setVerificationCode("");
    setBackupCodes([]);
    
    try {
      const response = await apiService.post('/auth/2fa/setup', { password: "" });
      
      if (response.secret) {
        setTwoFASecret(response.secret);
        // Guardar los backup codes temporalmente (no en backupCodes todavía)
        if (response.backupCodes) {
          setTempBackupCodes(response.backupCodes);
        }
        // Usar qrCodeUrl si está disponible, sino usar qrCode como fallback
        if (response.qrCodeUrl) {
          // Usar la URL de autenticación directamente para generar el QR
          setQrCodeData(response.qrCodeUrl);
        } else if (response.qrCode) {
          // Si solo tenemos el base64, mostrarlo como imagen (fallback)
          setQrCodeData(response.qrCode);
        }
        setShow2FAModal(true);
      }
    } catch (error) {
      toast.error(t('settings.messages.setupFailed'));
    } finally {
      setLoading2FA(false);
    }
  };

  // Habilitar 2FA
  const enable2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error(t('settings.messages.invalidCodeLength'));
      return;
    }

    setLoading2FA(true);
    try {
      const response = await apiService.post('/auth/2fa/enable', {
        token: verificationCode,
        backupCodes: tempBackupCodes // Usar los backup codes temporales
      });

      if (response.success) {
        setTwoFAEnabled(true);
        setBackupCodes(tempBackupCodes); // Mover los códigos temporales a los finales
        setShow2FASuccess(true); // Mostrar pantalla de éxito
        toast.success(t('settings.messages.twoFAEnabled'));
      }
    } catch (error) {
      toast.error(t('settings.messages.invalidCode'));
    } finally {
      setLoading2FA(false);
    }
  };

  // Deshabilitar 2FA
  const disable2FA = async () => {
    const password = window.prompt('Please enter your password to disable 2FA:');
    if (!password) {
      return;
    }

    setLoading2FA(true);
    try {
      await apiService.post('/auth/2fa/disable', { password });
      // Limpiar todos los estados de 2FA
      setTwoFAEnabled(false);
      setTwoFASecret("");
      setQrCodeData("");
      setBackupCodes([]);
      setTempBackupCodes([]);
      setShow2FASuccess(false);
      setVerificationCode("");
      toast.success(t('settings.messages.twoFADisabled'));
    } catch (error: any) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(t('settings.messages.disableFailed'));
      }
    } finally {
      setLoading2FA(false);
    }
  };

  // Cambiar idioma
  const handleLanguageChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLanguage = e.target.value;
    setLanguage(newLanguage);

    setSavingLanguage(true);
    try {
      await apiService.patch('/profile/me', {
        preferredLanguage: newLanguage
      });

      // Actualizar en localStorage para persistir
      const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        userObj.preferredLanguage = newLanguage;
        if (localStorage.getItem('user')) {
          localStorage.setItem('user', JSON.stringify(userObj));
        } else {
          sessionStorage.setItem('user', JSON.stringify(userObj));
        }
      }

      // Cambiar idioma en i18n inmediatamente
      await i18n.changeLanguage(newLanguage);

      toast.success(t('settings.languageUpdated'));
    } catch (error) {
      toast.error(t('settings.languageUpdateFailed'));
      // Revertir cambio si falla
      setLanguage(user?.preferredLanguage || "en");
    } finally {
      setSavingLanguage(false);
    }
  };

  // Cambiar contraseña
  const handleChangePassword = async () => {
    // Validaciones
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error(t('settings.messages.fillAllFields'));
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(t('settings.messages.passwordMismatch'));
      return;
    }

    if (newPassword.length < 8) {
      toast.error(t('settings.messages.passwordTooShort'));
      return;
    }

    setSavingPassword(true);
    try {
      await apiService.post('/auth/change-password', {
        currentPassword,
        newPassword
      });
      
      toast.success(t('settings.messages.passwordChanged'));
      setShowPasswordModal(false);
      // Limpiar formulario
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(t('settings.messages.passwordChangeFailed'));
      }
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto mt-6 p-6">
      <h1 className="text-3xl font-bold mb-8 text-gray-800">{t('settings.title')}</h1>

      <div className="space-y-6">
        {/* Account Information Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">{t('settings.accountInfo.title')}</h2>
          
          <div className="space-y-4">
            {/* Account Type */}
            <div className="flex items-center justify-between py-3 border-b">
              <div>
                <div className="font-medium text-gray-700">{t('settings.accountInfo.accountType')}</div>
                <div className="text-sm text-gray-500">{t('settings.accountInfo.accountTypeDesc')}</div>
              </div>
              <div className="text-gray-900 font-medium">
                {user?.accountType ? t(`settings.accountTypes.${user.accountType}`) : t('settings.accountTypes.STANDARD')}
              </div>
            </div>

            {/* Email */}
            <div className="flex items-center justify-between py-3 border-b">
              <div>
                <div className="font-medium text-gray-700">{t('settings.accountInfo.email')}</div>
                <div className="text-sm text-gray-500">{t('settings.accountInfo.emailDesc')}</div>
              </div>
              <div className="text-gray-900">{user?.email || "N/A"}</div>
            </div>

            {/* User ID */}
            <div className="flex items-center justify-between py-3">
              <div>
                <div className="font-medium text-gray-700">{t('settings.accountInfo.userId')}</div>
                <div className="text-sm text-gray-500">{t('settings.accountInfo.userIdDesc')}</div>
              </div>
              <div className="text-gray-900 font-mono text-sm">{user?.id || "N/A"}</div>
            </div>
          </div>
        </div>

        {/* Security Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">{t('settings.security.title')}</h2>
          
          <div className="space-y-4">
            {/* 2FA Toggle */}
            <div className="flex items-center justify-between py-3 border-b">
              <div>
                <div className="font-medium text-gray-700">{t('settings.security.twoFA')}</div>
                <div className="text-sm text-gray-500">
                  {t('settings.security.twoFADesc')}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {twoFAEnabled && (
                  <span className="text-green-600 text-sm font-medium">{t('settings.security.active')}</span>
                )}
                <button
                  onClick={() => twoFAEnabled ? disable2FA() : setup2FA()}
                  disabled={loading2FA}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    twoFAEnabled ? 'bg-green-500' : 'bg-gray-300'
                  } ${loading2FA ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      twoFAEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Change Password */}
            <div className="flex items-center justify-between py-3">
              <div>
                <div className="font-medium text-gray-700">Password</div>
                <div className="text-sm text-gray-500">Change your account password</div>
              </div>
              <button
                onClick={() => setShowPasswordModal(true)}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
              >
                Change Password
              </button>
            </div>
          </div>
        </div>

        {/* Preferences Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">Preferences</h2>
          
          <div className="space-y-4">
            {/* Language Selector */}
            <div className="flex items-center justify-between py-3">
              <div>
                <label htmlFor="language" className="font-medium text-gray-700">
                  Language
                </label>
                <div className="text-sm text-gray-500">Choose your preferred language</div>
              </div>
              <div className="relative">
                <select
                  id="language"
                  value={language}
                  onChange={handleLanguageChange}
                  disabled={savingLanguage}
                  className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.label}
                    </option>
                  ))}
                </select>
                {savingLanguage && (
                  <Loader2 className="absolute right-2 top-3 h-4 w-4 animate-spin text-gray-500" />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2FA Setup Modal */}
      {show2FAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Setup Two-Factor Authentication</h3>
            
            {!show2FASuccess ? (
              <>
                <p className="text-gray-600 mb-4">
                  Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                </p>
                
                {qrCodeData && (
                  <div className="flex justify-center mb-4">
                    <div className="border p-2 bg-white">
                      {qrCodeData.startsWith('data:image') ? (
                        // Si es base64, mostrar como imagen
                        <img src={qrCodeData} alt="2FA QR Code" style={{ width: 200, height: 200 }} />
                      ) : (
                        // Si es URL de autenticación, generar QR
                        <QRCodeCanvas value={qrCodeData} size={200} level="M" />
                      )}
                    </div>
                  </div>
                )}
                
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Or enter this code manually:</p>
                  <div className="bg-gray-100 p-2 rounded font-mono text-sm break-all">
                    {twoFASecret}
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">
                    Enter verification code from your app:
                  </label>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    maxLength={6}
                  />
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShow2FAModal(false);
                      setTwoFASecret("");
                      setQrCodeData("");
                      setVerificationCode("");
                      setTempBackupCodes([]);
                      setShow2FASuccess(false);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={enable2FA}
                    disabled={loading2FA || verificationCode.length !== 6}
                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {loading2FA ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Enable 2FA'
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="mb-4">
                  <p className="text-green-600 font-medium mb-3">
                    ✓ 2FA has been enabled successfully!
                  </p>
                  <p className="text-gray-600 mb-3">
                    Save these backup codes in a safe place. You can use them to access your account if you lose your authenticator device.
                  </p>
                  <div className="bg-gray-100 p-4 rounded-lg">
                    <p className="font-medium mb-2">Backup Codes:</p>
                    <div className="grid grid-cols-2 gap-2">
                      {backupCodes.map((code, index) => (
                        <div key={index} className="font-mono text-sm">
                          {code}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShow2FAModal(false);
                    // Limpiar solo estados temporales, mantener 2FA activado
                    setVerificationCode("");
                    setTwoFASecret("");
                    setQrCodeData("");
                    setTempBackupCodes([]);
                    setShow2FASuccess(false);
                    // backupCodes permanece para el usuario
                  }}
                  className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Change Password</h3>
            
            <div className="space-y-4">
              {/* Current Password */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-2 top-2.5 text-gray-500 hover:text-gray-700"
                  >
                    {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-2 top-2.5 text-gray-500 hover:text-gray-700"
                  >
                    {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-2 top-2.5 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Password Requirements */}
              <div className="text-sm text-gray-600">
                <p>Password must:</p>
                <ul className="list-disc list-inside">
                  <li>Be at least 8 characters long</li>
                  <li>Include uppercase and lowercase letters</li>
                  <li>Include at least one number</li>
                </ul>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setCurrentPassword("");
                  setNewPassword("");
                  setConfirmPassword("");
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleChangePassword}
                disabled={savingPassword || !currentPassword || !newPassword || !confirmPassword}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {savingPassword ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Change Password'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Setting;